// This code has been fully generated by an AI.
import { useEffect, useState } from 'react';
import { listRegistryUsers } from '../lib/api';
import type { RegistryUser } from '../lib/api';

const PAGE_SIZE = 25;

export default function AdminUsersPage() {
  const [users, setUsers] = useState<RegistryUser[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ totalAccounts: 0, adminAccounts: 0, attributedPlugins: 0 });
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listRegistryUsers({ page, limit: PAGE_SIZE, search })
      .then((result) => {
        if (cancelled) return;
        setUsers(result.data ?? []);
        setTotal(result.pagination?.total ?? 0);
        setSummary(result.summary);
        setError('');
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Could not load users.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [page, search]);

  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">Users</h1>
          <p className="muted text-sm m-0">Registry accounts and their recent activity</p>
        </div>
        <span className="pill pill--accent pill--roomy">ADMIN ONLY</span>
      </div>
      <div className="page__body">
        <div className="stats-grid mb-3">
          <article className="stat-card">
            <span className="stat-card__label">Tracked accounts</span>
            <strong className="stat-card__value">{summary.totalAccounts.toLocaleString()}</strong>
          </article>
          <article className="stat-card">
            <span className="stat-card__label">Admin accounts</span>
            <strong className="stat-card__value">{summary.adminAccounts.toLocaleString()}</strong>
          </article>
          <article className="stat-card">
            <span className="stat-card__label">Attributed plugins</span>
            <strong className="stat-card__value">{summary.attributedPlugins.toLocaleString()}</strong>
          </article>
        </div>
        <p className="muted text-xs mb-2">
          Counts cover the full directory and do not change with search or pagination. Accounts are tracked from sign-in; plugin totals include active and pending plugins attributed to those accounts.
        </p>
        <div className="search-bar mb-2">
          <input
            className="search-input"
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search login or name…"
            aria-label="Search users by login or name"
          />
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => { setSearchInput(''); setPage(1); }}
            disabled={!searchInput && !search}
          >
            Clear
          </button>
        </div>

        {error && <div className="alert alert--error" role="alert">{error}</div>}
        {loading && <div className="centered-status" role="status"><span className="spinner" aria-hidden="true" /> Loading users…</div>}
        {!loading && !error && users.length === 0 && (
          <div className="empty-state">
            <h2>No accounts found</h2>
            <p>{search ? 'Try another login or name.' : 'Accounts appear after their first sign-in.'}</p>
          </div>
        )}
        {!loading && !error && users.length > 0 && (
          <>
            <div className="table-wrap" tabIndex={0} aria-label="Registered users">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Account</th>
                    <th scope="col">Role at sign-in</th>
                    <th scope="col">Plugins</th>
                    <th scope="col">Pending</th>
                    <th scope="col">First seen</th>
                    <th scope="col">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.login}>
                      <td>
                        <div className="user-directory__account">
                          {user.avatarUrl && <img src={user.avatarUrl} alt="" loading="lazy" />}
                          <div>
                            <a href={`https://github.com/${encodeURIComponent(user.login)}`} target="_blank" rel="noopener noreferrer">
                              @{user.login}
                            </a>
                            {user.name && <div className="muted text-xs">{user.name}</div>}
                          </div>
                        </div>
                      </td>
                      <td><span className={`pill ${user.role === 'admin' ? 'pill--accent' : ''}`}>{user.role}</span></td>
                      <td>{user.pluginCount}</td>
                      <td>{user.pendingPluginCount}</td>
                      <td>{formatDate(user.firstSeenAt)}</td>
                      <td>{formatDate(user.lastSeenAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination mt-2">
              <span className="muted text-sm">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
              </span>
              <div className="flex gap-sm">
                <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</button>
                <span className="muted text-sm">Page {page} of {lastPage}</span>
                <button type="button" className="btn btn--secondary btn--sm" disabled={page >= lastPage} onClick={() => setPage((value) => value + 1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
}
